import { Dispute } from 'APIs/disputes/disputes.types';
import { AnyMerchant } from 'APIs/merchants/merchants.types';
import BackComponent from 'Components/BackComponent/BackComponent';
import FilterComponent from 'Components/Common/Filters/FilterComponent';
import { FilterOption } from 'Components/Common/Filters/types';
import HeaderTitle from 'Components/HeaderTitle/HeaderTitle';
import { TransactionAPIResponseType } from 'Components/Transactions/types';
import { FunctionComponent, MouseEvent, useState } from 'react';
import { MdFilterList, MdRefresh } from 'react-icons/md';

import DisputeDataTable from './DisputeDataTable';
import DisputeDetails from './DisputeDetails';
import SetDisputeOutcomeModal from './SetDisputeOutcomeModal';
import { FilterContainer } from './styles';
import { SetDisputeOutcomeFormProps, toSetDisputeOutcomeFormProps } from './types';

type Props = {
  disputes: Array<Dispute>;
  totalCount: number;
  perPage: number;
  isLoading: boolean;
  activePage: number;
  fetchingError: string;
  showFilterButton: boolean;
  initFilterOptions: FilterOption[];
  filterOptionValues: FilterOption[];
  modalErrorMsg: string;
  modalDispute: Dispute | null;
  modalLoading: boolean;
  detailsDispute: Dispute | null;
  payment: TransactionAPIResponseType | null;
  merchant: AnyMerchant | null;
  fetchDisputes: () => void;
  setActivePage: (page: number) => void;
  handlePageChange: (page: number) => void;
  handlePerPageChange: (newPerPage: number, page: number) => void;
  setDetailsDispute: (dispute: Dispute | null) => void;
  setModalDispute: (dispute: Dispute | null) => void;
  handleClickBack: (e: MouseEvent<HTMLButtonElement>) => void;
  updateDispute: (formData: SetDisputeOutcomeFormProps) => void;
  setFilterOptionValues: (options: FilterOption[]) => void;
};

const DisputesList = ({
  detailsDispute,
  fetchDisputes,
  setActivePage,
  disputes,
  totalCount,
  perPage,
  isLoading,
  handlePageChange,
  handlePerPageChange,
  activePage,
  fetchingError,
  setDetailsDispute,
  handleClickBack,
  modalErrorMsg,
  modalLoading,
  payment,
  merchant,
  updateDispute,
  showFilterButton,
  initFilterOptions,
  filterOptionValues,
  setFilterOptionValues,
  setModalDispute,
  modalDispute,
}: Props) => {
  const RefreshIcon = MdRefresh as FunctionComponent;
  const FilterListIcon = MdFilterList as FunctionComponent;

  const [filterPopupOpen, setFilterPopupOpen] = useState<boolean>(false);
  const [filterCount, setFilterCount] = useState<number>(
    initFilterOptions?.filter((option) => option.checked).length ?? 0,
  );

  return (
    <>
      {!detailsDispute && (
        <div>
          <h3 className="mb-0">Disputes</h3>
          {showFilterButton && (
            <FilterContainer>
              <button className="lp-w-full" onClick={() => fetchDisputes()}>
                Refresh <RefreshIcon />
              </button>
              <button className="lp-w-full" onClick={() => setFilterPopupOpen((prev) => !prev)}>
                Filter {filterCount > 0 && `(${filterCount})`} <FilterListIcon />
              </button>
              {filterPopupOpen && (
                <FilterComponent
                  filterOptionValues={filterOptionValues}
                  initFilterOptionValues={initFilterOptions}
                  setFilterOptionValues={setFilterOptionValues}
                  handlePageChange={setActivePage}
                  handleFilterCount={setFilterCount}
                  toggleFilter={() => setFilterPopupOpen(false)}
                />
              )}
            </FilterContainer>
          )}
          <DisputeDataTable
            disputeList={disputes}
            totalRows={totalCount}
            limit={perPage}
            activePage={activePage}
            fetchListLoader={isLoading}
            isErrorRequest={fetchingError !== ''}
            handlePerRowsChange={handlePerPageChange}
            handlePageChange={handlePageChange}
            setDetailsDispute={setDetailsDispute}
            setModalDispute={setModalDispute}
          />
        </div>
      )}
      {detailsDispute && (
        <>
          <HeaderTitle title="Dispute details" />
          <BackComponent text="Back to disputes" handleClickBack={handleClickBack} />
          <DisputeDetails
            dispute={detailsDispute}
            payment={payment}
            merchant={merchant}
            updateDispute={updateDispute}
            setModalDispute={setModalDispute}
          />
        </>
      )}
      {modalDispute && (
        <SetDisputeOutcomeModal
          setDisputeOutcomeFormProps={toSetDisputeOutcomeFormProps(modalDispute)}
          updateDisputeHendler={updateDispute}
          modalErrorMsg={modalErrorMsg}
          isModalLoading={modalLoading}
          setModalDispute={setModalDispute}
        />
      )}
    </>
  );
};

export default DisputesList;
